<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { LockClosedIcon } from '@heroicons/vue/24/outline'
import { putWithToken } from '../../lib/fetch-api'
import { calculateMD5 } from '../../lib/auth'

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const error = ref('')
const success = ref('')
const isPending = ref(false)

const router = useRouter()

const handleSubmit = async () => {
  error.value = ''
  success.value = ''

  if (
    !currentPassword.value ||
    !newPassword.value ||
    !confirmPassword.value
  ) {
    error.value = 'Complete all password fields.'
    return
  }

  if (newPassword.value.length < 8) {
    error.value = 'The new password must contain at least 8 characters.'
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    error.value = 'The new passwords do not match.'
    return
  }

  if (currentPassword.value === newPassword.value) {
    error.value = 'The new password must be different from the current password.'
    return
  }

  isPending.value = true

  try {
    const response = await putWithToken(
      'users/change-password',
      {
        current_password: calculateMD5(currentPassword.value),
        new_password: calculateMD5(newPassword.value)
      }
    )

    if (!response.isSuccess) {
      error.value = response.message || 'Password change failed.'
      return
    }

    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''

    success.value =
      'Password changed successfully. Please sign in with your new password.'

    window.setTimeout(() => {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      router.push('/login')
    }, 1500)
  } catch (requestError) {
    error.value =
      requestError?.message || 'An error occurred while changing the password.'
  } finally {
    isPending.value = false
  }
}
</script>

<template>
  <div class="mx-auto w-full max-w-xl rounded-lg bg-white p-8 shadow-sm">
    <h1 class="mb-2 text-center text-3xl font-bold text-blue-700">
      Change Password
    </h1>

    <p class="mb-6 text-center text-sm text-gray-600">
      Enter your current password, then choose a new password.
    </p>

    <div
      v-if="error"
      role="alert"
      class="mb-4 rounded-md bg-red-100 p-3 text-red-700"
    >
      {{ error }}
    </div>

    <div
      v-if="success"
      role="status"
      class="mb-4 rounded-md bg-green-100 p-3 text-green-700"
    >
      {{ success }}
    </div>

    <form
      class="space-y-5"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div>
        <label
          for="current-password"
          class="mb-1 block text-sm font-medium text-gray-700"
        >
          Current password
        </label>

        <div class="relative">
          <LockClosedIcon
            class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-600"
          />

          <input
            id="current-password"
            v-model="currentPassword"
            type="password"
            autocomplete="current-password"
            class="w-full rounded-md border border-gray-300 py-3 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label
          for="new-password"
          class="mb-1 block text-sm font-medium text-gray-700"
        >
          New password
        </label>

        <div class="relative">
          <LockClosedIcon
            class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-600"
          />

          <input
            id="new-password"
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-md border border-gray-300 py-3 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p class="mt-1 text-xs text-gray-500">
          Use at least 8 characters.
        </p>
      </div>

      <div>
        <label
          for="confirm-new-password"
          class="mb-1 block text-sm font-medium text-gray-700"
        >
          Confirm new password
        </label>

        <div class="relative">
          <LockClosedIcon
            class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-600"
          />

          <input
            id="confirm-new-password"
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-md border border-gray-300 py-3 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <button
        type="submit"
        :disabled="isPending || Boolean(success)"
        class="w-full rounded-md bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ isPending ? 'Changing password...' : 'Change Password' }}
      </button>
    </form>
  </div>
</template>
